import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Project {
  id: string; name: string; slug: string; description: string | null
  framework: string | null; author_name: string; likes: number; views: number; created_at: string
}

const FRAMEWORKS = ['All', 'react', 'nextjs', 'vue', 'node', 'python', 'html', 'typescript']
const FW_ICONS: Record<string, string> = { react: '⚛️', nextjs: '▲', vue: '💚', node: '🟢', python: '🐍', html: '🌐', typescript: '🔷' }

export function CommunityPage() {
  const [projects, setProjects] = useState<Project[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [framework, setFramework] = useState('')
  const [sort, setSort] = useState<'recent' | 'popular'>('popular')
  const [forking, setForking] = useState<string | null>(null)
  const [showPublish, setShowPublish] = useState(false)

  const load = useCallback(() => {
    const params = new URLSearchParams({ sort })
    if (framework) params.set('framework', framework)
    if (search) params.set('search', search)
    fetch(apiUrl(`/api/community?${params}`)).then(r => r.json()).then(data => {
      setProjects(data.projects ?? [])
      setTotal(data.total ?? 0)
    }).catch(() => {})
  }, [sort, framework, search])

  useEffect(() => { load() }, [load])

  const fork = async (id: string) => {
    setForking(id)
    try { await authFetch(apiUrl(`/api/community/${id}/fork`), { method: 'POST' }); load() } catch {}
    setForking(null)
  }

  const like = async (id: string) => {
    await authFetch(apiUrl(`/api/community/${id}/like`), { method: 'POST' }).catch(() => {})
    load()
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', background: 'var(--bg-0)', padding: '32px 28px 80px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 22, color: 'var(--text-0)', marginBottom: 4 }}>
            🌍 Explore Community
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-mono)' }}>
            {total} public projects · Fork, learn, and build together
          </div>
        </div>
        <button onClick={() => setShowPublish(true)} style={{
          padding: '8px 16px', borderRadius: 8, border: '1px solid var(--accent)',
          background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', cursor: 'pointer',
          fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
        }}>+ Publish Project</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} style={{
          padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-2)',
          background: 'var(--bg-2)', color: 'var(--text-0)', fontSize: 11,
          fontFamily: 'var(--font-mono)', outline: 'none', width: 200,
        }} />
        {FRAMEWORKS.map(f => (
          <button key={f} onClick={() => setFramework(f === 'All' ? '' : f)} style={{
            padding: '4px 10px', borderRadius: 20, border: '1px solid var(--border-2)',
            background: (f === 'All' ? !framework : framework === f) ? 'rgba(124,110,245,0.15)' : 'none',
            color: (f === 'All' ? !framework : framework === f) ? 'var(--accent)' : 'var(--text-2)',
            cursor: 'pointer', fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 600,
          }}>
            {FW_ICONS[f] ?? ''} {f}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <select value={sort} onChange={e => setSort(e.target.value as any)} style={{
          padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-2)',
          background: 'var(--bg-2)', color: 'var(--text-1)', fontSize: 10,
          fontFamily: 'var(--font-mono)', cursor: 'pointer', outline: 'none',
        }}>
          <option value="popular">🔥 Popular</option>
          <option value="recent">🕐 Recent</option>
        </select>
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {projects.map(p => (
          <div key={p.id} style={{
            background: 'var(--bg-1)', border: '1px solid var(--border)',
            borderRadius: 12, padding: 16, transition: 'all 0.15s', cursor: 'default',
          }} onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-2)' }}
             onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)' }}>
            {/* Top */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8, background: 'var(--bg-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, color: 'var(--accent)', flexShrink: 0,
              }}>
                {FW_ICONS[p.framework ?? ''] ?? p.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: 'var(--text-0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>by {p.author_name ?? 'Unknown'}</div>
              </div>
            </div>

            {/* Description */}
            {p.description && (
              <div style={{ fontSize: 11, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', marginBottom: 12, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {p.description}
              </div>
            )}

            {/* Tags + stats */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {p.framework && (
                <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 10, background: 'var(--bg-3)', color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {p.framework}
                </span>
              )}
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>👁 {formatNum(p.views)}</span>
              <button onClick={() => like(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 9, color: 'var(--red)', fontFamily: 'var(--font-mono)', padding: 0 }}>
                ❤️ {formatNum(p.likes)}
              </button>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button onClick={() => fork(p.id)} disabled={forking === p.id} style={{
                flex: 1, padding: '6px', borderRadius: 6, border: '1px solid var(--border-2)',
                background: 'none', color: 'var(--text-1)', cursor: 'pointer',
                fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 600,
                opacity: forking === p.id ? 0.5 : 1, transition: 'all 0.12s',
              }}>
                {forking === p.id ? 'Forking...' : '🍴 Fork'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {projects.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
          No public projects found. Be the first to publish!
        </div>
      )}

      {/* Publish Modal */}
      {showPublish && <PublishModal onClose={() => setShowPublish(false)} onPublished={() => { setShowPublish(false); load() }} />}
    </div>
  )
}

function PublishModal({ onClose, onPublished }: { onClose: () => void; onPublished: () => void }) {
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [framework, setFramework] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState('')

  const publish = async () => {
    if (!name.trim()) { setError('Name required'); return }
    setPublishing(true); setError('')
    try {
      const res = await authFetch(apiUrl('/api/community/publish'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description: desc, framework: framework || null }),
      })
      if (!res.ok) { const d = await res.json(); setError(d.error ?? 'Failed'); return }
      onPublished()
    } catch (e: any) { setError(e.message) }
    finally { setPublishing(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 380, background: 'var(--bg-1)', border: '1px solid var(--border-2)', borderRadius: 14, padding: 24, boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--text-0)', marginBottom: 16 }}>Publish to Community</div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Project Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="My Awesome App" style={inputStyle} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Description</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="What does it do?" style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Framework</label>
          <select value={framework} onChange={e => setFramework(e.target.value)} style={inputStyle}>
            <option value="">None</option>
            <option value="react">React</option>
            <option value="nextjs">Next.js</option>
            <option value="vue">Vue</option>
            <option value="node">Node.js</option>
            <option value="python">Python</option>
            <option value="html">HTML/CSS</option>
            <option value="typescript">TypeScript</option>
          </select>
        </div>
        {error && <div style={{ color: 'var(--red)', fontSize: 10, marginBottom: 8, fontFamily: 'var(--font-mono)' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid var(--border-2)', background: 'none', color: 'var(--text-2)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Cancel</button>
          <button onClick={publish} disabled={publishing} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>
            {publishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatNum(n: number): string {
  if (!n) return '0'
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
  return String(n)
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none' }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }
